import { ROUTES } from "@/constants/routes";
import { matchPath } from "react-router-dom";

/**
 * Stable, content-free names for pageviews. React 19 hoists user content
 * (canvas/workspace/skill names) into <title>, so pageviews must never use
 * document.title.
 */

const flattenRoutes = (): [string, string][] => {
  const entries: [string, string][] = [];
  for (const [groupKey, group] of Object.entries(ROUTES)) {
    if (typeof group === "string") {
      entries.push([groupKey.toLowerCase(), group]);
      continue;
    }
    for (const [leafKey, pattern] of Object.entries(group)) {
      entries.push([`${groupKey}_${leafKey}`.toLowerCase(), pattern as string]);
    }
  }
  return entries;
};

const ROUTE_NAMES: [string, string][] = flattenRoutes();

export function routeNameFor(pathname: string): string {
  for (const [name, pattern] of ROUTE_NAMES) {
    if (matchPath({ path: pattern, end: true }, pathname)) return name;
  }
  // Settings sub-paths render inside the shell route; anything else unknown.
  if (matchPath({ path: "/settings/*", end: false }, pathname)) {
    return "settings";
  }
  return "unknown";
}

/** Right-pane route patterns → pane_type (mirrors RightPaneRouter.tsx). */
export const RIGHT_PANE_ROUTE_PATTERNS: [string, string][] = [
  ["session", ROUTES.SESSIONS.SESSION],
  ["file_record", ROUTES.FILE_RECORDS.DETAIL],
  ["entities", ROUTES.ENTITIES.LIST],
  ["entity_detail", ROUTES.ENTITIES.DETAIL],
  ["notion", ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL],
  ["email", ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL],
  ["google_drive_file", ROUTES.GOOGLE_DRIVE.FILE_DETAIL],
  ["github_issue", ROUTES.GITHUB.ISSUE_DETAIL],
  ["github_pr", ROUTES.GITHUB.PR_DETAIL],
  ["github_repo", ROUTES.GITHUB.REPO_DETAIL],
  ["github_project", ROUTES.GITHUB.PROJECT_DETAIL],
  ["calendar_event", ROUTES.GOOGLE_CALENDAR.EVENT_DETAIL],
  ["slack_message", ROUTES.SLACK.MESSAGE_DETAIL],
  ["granola_meeting", ROUTES.GRANOLA.MEETING_DETAIL],
  ["skill", ROUTES.SKILLS.SHOW],
  ["skill_template", ROUTES.SKILL_TEMPLATES.SHOW],
  ["automation", ROUTES.AUTOMATIONS.SHOW],
  ["connection", ROUTES.SETTINGS.INTEGRATION_SETTINGS],
];

export function paneTypeFor(
  panePathname: string | undefined | null,
): string | undefined {
  if (!panePathname) return undefined;
  for (const [paneType, pattern] of RIGHT_PANE_ROUTE_PATTERNS) {
    if (matchPath({ path: pattern, end: true }, panePathname)) {
      return paneType;
    }
  }
  return "unknown";
}
